import React, { useEffect, useState } from "react";
import {
  Table,
  Tabs,
  Input,
  Spin,
  Alert,
  Button,
  Card,
  Space,
  Tag,
  Avatar,
  Typography,
  Badge,
  Statistic,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EyeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  WarningOutlined,
  StopOutlined,
  ReloadOutlined,
  FireOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { TabPane } = Tabs;
const { Search } = Input;
const { Title, Text } = Typography;

function UserVerifications() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [filteredVerifications, setFilteredVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [allVerifications, setAllVerifications] = useState({});

  const verificationsPerPage = 15;

  // Messages temporaires
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  // Charge toutes les données
  const fetchAllVerifications = async () => {
    try {
      setLoading(true);

      const endpoints = {
        all: "/submit/verification/all",
        pending: "/submit/verification/pending",
        approved: "/submit/verification/approved",
        rejected: "/submit/verification/rejected",
        not_submitted: "/submit/verification/not_submitted",
      };

      const responses = await Promise.all(
        Object.entries(endpoints).map(async ([key, endpoint]) => {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/api${endpoint}`
          );
          if (!res.ok) throw new Error(`Erreur API ${key}`);
          const data = await res.json();

          const withStatus = (data.data || []).map((item) => ({
            ...item,
            status: item.status || key,
          }));

          return [
            key,
            { data: withStatus, total: data.total || withStatus.length },
          ];
        })
      );

      const result = Object.fromEntries(responses);
      setAllVerifications(result);

      setVerifications(result.all.data || []);
      setFilteredVerifications(result.all.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error(error);
      showMessage("Erreur lors du chargement des données", "error");
      setAllVerifications({});
      setVerifications([]);
      setFilteredVerifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllVerifications();
  }, []);

  useEffect(() => {
    if (allVerifications[statusFilter]) {
      setVerifications(allVerifications[statusFilter].data || []);
      setFilteredVerifications(allVerifications[statusFilter].data || []);
      setCurrentPage(1);
    }
  }, [statusFilter, allVerifications]);

  useEffect(() => {
    const filtered = verifications.filter((item) => {
      const { fullname, phone, email } = item.user || {};
      const search = searchTerm.toLowerCase();
      return (
        fullname?.toLowerCase().includes(search) ||
        phone?.toLowerCase().includes(search) ||
        email?.toLowerCase().includes(search)
      );
    });
    setFilteredVerifications(filtered);
    setCurrentPage(1);
  }, [searchTerm, verifications]);

  // ✅ Fonction pour rendre le statut avec indicateur de resoumission
  const renderStatus = (record) => {
    const status = record.status || "not_submitted";
    const isResubmission = record.is_resubmission || false;

    const statusConfig = {
      pending: {
        color: isResubmission ? "#ff4d4f" : "orange",
        text: isResubmission ? "Resoumission en attente" : "En attente",
        icon: isResubmission ? (
          <ReloadOutlined spin />
        ) : (
          <ClockCircleOutlined />
        ),
      },
      approved: {
        color: "green",
        text: "Approuvée",
        icon: <CheckCircleOutlined />,
      },
      rejected: {
        color: "red",
        text: "Rejetée",
        icon: <CloseCircleOutlined />,
      },
      not_submitted: {
        color: "default",
        text: "Non soumise",
        icon: <FileTextOutlined />,
      },
    };

    const config = statusConfig[status] || statusConfig.not_submitted;

    return (
      <Space direction="vertical" size={4}>
        <Tag
          icon={config.icon}
          color={config.color}
          style={{ fontSize: "13px" }}
        >
          {config.text}
        </Tag>
        {/* ✅ Badge urgent si resoumission */}
        {isResubmission && (
          <Tag
            icon={<FireOutlined />}
            color="volcano"
            style={{ fontSize: "11px" }}
          >
            Urgent - Tentative {record.rejection_count + 1}
          </Tag>
        )}
      </Space>
    );
  };

  // ✅ Badge de rejets amélioré
  const renderRejectionBadge = (record) => {
    const count = record.rejection_count || 0;
    const hasReachedLimit = record.has_reached_limit || false;
    const isResubmission = record.is_resubmission || false;

    if (count === 0) return null;

    if (hasReachedLimit) {
      return (
        <Tooltip title="Compte bloqué après 3 rejets">
          <Badge
            count={
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <StopOutlined style={{ fontSize: 12 }} />
                Bloqué
              </span>
            }
            style={{ backgroundColor: "#ff4d4f" }}
          />
        </Tooltip>
      );
    }

    if (isResubmission) {
      return (
        <Tooltip title={`${count} rejet(s) - Nouvelle tentative en cours`}>
          <Badge
            count={`${count}/3 - Nouvelle tentative`}
            style={{
              backgroundColor: "#ff4d4f",
              animation: "pulse 2s infinite",
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`${count} rejet(s) sur 3 maximum`}>
        <Badge
          count={`${count}/3 rejets`}
          style={{
            backgroundColor: count >= 2 ? "#ff4d4f" : "#faad14",
          }}
        />
      </Tooltip>
    );
  };

  // ✅ Colonnes avec indicateurs de resoumission
  const columns = [
    {
      title: "Utilisateur",
      key: "user",
      width: 240,
      render: (_, record) => {
        const isResubmission = record.is_resubmission || false;

        return (
          <Space>
            <Badge
              dot={isResubmission}
              status={isResubmission ? "error" : "default"}
              offset={[-5, 5]}
            >
              <Avatar
                src={record.photos?.profile?.photo}
                icon={<UserOutlined />}
                size="large"
                style={{
                  border: isResubmission
                    ? "3px solid #ff4d4f"
                    : "2px solid #1890ff",
                }}
              />
            </Badge>
            <div>
              <div style={{ fontWeight: "600", fontSize: "14px" }}>
                {record.user?.fullname || "N/A"}
                {isResubmission && (
                  <Tooltip title="Nouvelle soumission après rejet">
                    <FireOutlined
                      style={{
                        marginLeft: 8,
                        color: "#ff4d4f",
                        animation: "pulse 2s infinite",
                      }}
                    />
                  </Tooltip>
                )}
              </div>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {record.user?.user_uuid?.slice(0, 8)}...
              </Text>
              {record.user?.status === "blocked" && (
                <div style={{ marginTop: 4 }}>
                  <Tag
                    color="red"
                    icon={<StopOutlined />}
                    style={{ fontSize: "11px" }}
                  >
                    Compte bloqué
                  </Tag>
                </div>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: "Contact",
      key: "contact",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 6 }}>
            <PhoneOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            <Text style={{ fontSize: "13px" }}>
              {record.user?.phone || "N/A"}
            </Text>
          </div>
          <div>
            <MailOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            <Text style={{ fontSize: "13px" }}>
              {record.user?.email || "Non renseigné"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Statut",
      key: "status",
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {renderStatus(record)}
          {renderRejectionBadge(record)}
        </Space>
      ),
      filters: [
        { text: "En attente", value: "pending" },
        { text: "Approuvée", value: "approved" },
        { text: "Rejetée", value: "rejected" },
        { text: "Non soumise", value: "not_submitted" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Documents",
      key: "documents",
      width: 150,
      render: (_, record) => {
        const docs = [];
        if (record.photos?.profile?.photo) docs.push("Profil");
        if (record.photos?.id_card_front?.card_front) docs.push("Recto");
        if (record.photos?.id_card_back?.card_back) docs.push("Verso");

        const total = 3;
        const uploaded = docs.length;

        return (
          <div>
            <div style={{ marginBottom: 6 }}>
              <Text
                style={{
                  fontSize: "13px",
                  fontWeight: uploaded === total ? "600" : "normal",
                }}
              >
                {uploaded}/{total} documents
              </Text>
            </div>
            <Space size={4} wrap>
              {docs.map((doc) => (
                <Tag
                  key={doc}
                  color="blue"
                  style={{ fontSize: "11px", margin: 0 }}
                >
                  {doc}
                </Tag>
              ))}
            </Space>
          </div>
        );
      },
    },
    {
      title: "Date soumission",
      dataIndex: "submitted_at",
      key: "submitted_at",
      width: 150,
      render: (text, record) => {
        const isResubmission = record.is_resubmission || false;
        const days = record.days_since_submission || 0;

        return (
          <Space direction="vertical" size={4}>
            <Space>
              <CalendarOutlined style={{ color: "#1890ff" }} />
              <Text style={{ fontSize: "13px" }}>
                {text ? new Date(text).toLocaleDateString("fr-FR") : "—"}
              </Text>
            </Space>
            {isResubmission && days !== null && (
              <Text
                type={days > 2 ? "danger" : "warning"}
                style={{ fontSize: "11px" }}
              >
                {days < 1
                  ? "Aujourd'hui"
                  : `Il y a ${Math.floor(days)} jour(s)`}
              </Text>
            )}
          </Space>
        );
      },
      sorter: (a, b) => {
        if (!a.submitted_at) return 1;
        if (!b.submitted_at) return -1;
        return new Date(a.submitted_at) - new Date(b.submitted_at);
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const isResubmission = record.is_resubmission || false;

        return (
          <Button
            type={isResubmission ? "primary" : "default"}
            danger={isResubmission}
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/verification/${record.user.user_uuid}`);
            }}
            style={{ width: "100%" }}
          >
            {isResubmission ? "Urgent" : "Détails"}
          </Button>
        );
      },
    },
  ];

  const startIndex = (currentPage - 1) * verificationsPerPage;
  const endIndex = startIndex + verificationsPerPage;
  const paginatedData = filteredVerifications.slice(startIndex, endIndex);

  const handleRowClick = (record) => {
    navigate(`/verification/${record.user.user_uuid}`);
  };

  const getTabCounts = () => {
    return {
      all: allVerifications.all?.total || 0,
      pending: allVerifications.pending?.total || 0,
      approved: allVerifications.approved?.total || 0,
      rejected: allVerifications.rejected?.total || 0,
      not_submitted: allVerifications.not_submitted?.total || 0,
    };
  };

  const tabCounts = getTabCounts();

  // ✅ Compter les resoumissions urgentes
  const resubmissionsCount = verifications.filter(
    (v) => v.is_resubmission
  ).length;

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
      }}
    >
      {/* ✅ Alerte resoumissions urgentes */}
      {resubmissionsCount > 0 && (
        <Alert
          message={`${resubmissionsCount} resoumission(s) en attente de validation`}
          description="Ces utilisateurs ont resoumis leurs documents après un rejet et attendent une nouvelle validation."
          type="error"
          showIcon
          icon={<FireOutlined />}
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Statistiques */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total"
              value={tabCounts.all}
              valueStyle={{ color: "#1890ff" }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="En attente"
              value={tabCounts.pending}
              valueStyle={{ color: "#faad14" }}
              prefix={<ClockCircleOutlined />}
              suffix={
                resubmissionsCount > 0 && (
                  <Tooltip title="Dont resoumissions">
                    <Badge
                      count={resubmissionsCount}
                      style={{ backgroundColor: "#ff4d4f" }}
                    />
                  </Tooltip>
                )
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Approuvées"
              value={tabCounts.approved}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Rejetées"
              value={tabCounts.rejected}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Non soumises"
              value={tabCounts.not_submitted}
              valueStyle={{ color: "#8c8c8c" }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Carte principale */}
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
              <UserOutlined style={{ marginRight: 12, color: "#1890ff" }} />
              Vérifications d'identité
            </Title>
            <Text type="secondary" style={{ fontSize: "15px" }}>
              Gérer les demandes de vérification KYC des utilisateurs
            </Text>
          </div>

          {message.text && (
            <Alert
              message={message.text}
              type={message.type}
              showIcon
              closable
            />
          )}

          <Tabs
            activeKey={statusFilter}
            onChange={setStatusFilter}
            size="large"
            type="card"
          >
            <TabPane
              tab={
                <Space>
                  <FileTextOutlined />
                  Toutes
                  <Badge
                    count={tabCounts.all}
                    style={{ backgroundColor: "#1890ff" }}
                  />
                </Space>
              }
              key="all"
            />
            <TabPane
              tab={
                <Space>
                  <ClockCircleOutlined />
                  En attente
                  <Badge
                    count={tabCounts.pending}
                    style={{ backgroundColor: "#faad14" }}
                  />
                </Space>
              }
              key="pending"
            />
            <TabPane
              tab={
                <Space>
                  <CheckCircleOutlined />
                  Approuvées
                  <Badge
                    count={tabCounts.approved}
                    style={{ backgroundColor: "#52c41a" }}
                  />
                </Space>
              }
              key="approved"
            />
            <TabPane
              tab={
                <Space>
                  <CloseCircleOutlined />
                  Rejetées
                  <Badge
                    count={tabCounts.rejected}
                    style={{ backgroundColor: "#ff4d4f" }}
                  />
                </Space>
              }
              key="rejected"
            />
            <TabPane
              tab={
                <Space>
                  <FileTextOutlined />
                  Non soumises
                  <Badge count={tabCounts.not_submitted} />
                </Space>
              }
              key="not_submitted"
            />
          </Tabs>

          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Search
              placeholder="Rechercher par nom, téléphone ou email"
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 400 }}
              allowClear
              size="large"
              prefix={<UserOutlined />}
            />
            <div>
              <Text strong style={{ fontSize: "15px" }}>
                {filteredVerifications.length} résultat(s)
              </Text>
            </div>
          </Space>

          {loading ? (
            <div style={{ textAlign: "center", padding: "80px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 20 }}>
                <Text type="secondary" style={{ fontSize: "15px" }}>
                  Chargement des vérifications...
                </Text>
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={paginatedData}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize: verificationsPerPage,
                total: filteredVerifications.length,
                onChange: (page) => setCurrentPage(page),
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} sur ${total} élément(s)`,
                position: ["bottomCenter"],
              }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: {
                  cursor: "pointer",
                  backgroundColor: record.is_resubmission
                    ? "#fff1f0"
                    : undefined,
                },
              })}
              scroll={{ x: 1300 }}
              rowClassName={(record, index) => {
                if (record.is_resubmission) return "table-row-resubmission";
                return index % 2 === 0 ? "table-row-light" : "table-row-dark";
              }}
              size="middle"
              bordered
            />
          )}
        </Space>
      </Card>

      {/* ✅ Styles CSS avec animation */}
      <style jsx>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-light:hover {
          background-color: #f5f5f5 !important;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-dark:hover {
          background-color: #f0f0f0 !important;
        }
        .table-row-resubmission {
          background-color: #fff1f0 !important;
          border-left: 4px solid #ff4d4f;
        }
        .table-row-resubmission:hover {
          background-color: #ffe7e6 !important;
        }
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}

export default UserVerifications;
